import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import Fragment from "sap/ui/core/Fragment";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import JSONModel from "sap/ui/model/json/JSONModel";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { Select$ChangeEvent } from "sap/m/Select";
import type { CheckBox$SelectEvent } from "sap/m/CheckBox";
import type Dialog from "sap/m/Dialog";
import type Select from "sap/m/Select";
import type TextArea from "sap/m/TextArea";
import type Text from "sap/m/Text";
import type Table from "sap/m/Table";
import type ColumnListItem from "sap/m/ColumnListItem";
import type Context from "sap/ui/model/odata/v4/Context";
import type ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import type { Button$PressEvent } from "sap/m/Button";
import type { ListBase$SelectionChangeEvent } from "sap/m/ListBase";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

type StatusKey = "ALL" | "Active" | "PendingEnrollment" | "Decommissioned" | "EnrollmentExpired";
type EnvKey = "ALL" | "Production" | "Test";

interface ViewState {
	statusFilter: StatusKey;
	environmentFilter: EnvKey;
	searchQuery: string;
	noOwnerOnly: boolean;
}

interface VariantPreset {
	status: StatusKey;
	environment: EnvKey;
	search: string;
}

const VARIANTS: Record<string, VariantPreset> = {
	"standard":       { status: "ALL",               environment: "ALL", search: "" },
	"active":         { status: "Active",            environment: "ALL", search: "" },
	"pending":        { status: "PendingEnrollment", environment: "ALL", search: "" },
	"decommissioned": { status: "Decommissioned",    environment: "ALL", search: "" }
};

const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "deployments.stats.total.label", subtitleKey: "deployments.stats.total.subtitle", state: "Neutral" },
	{ key: "active", labelKey: "deployments.stats.active.label", subtitleKey: "deployments.stats.active.subtitle", state: "Good", filter: "Status eq 'Active'" },
	{ key: "pending", labelKey: "deployments.stats.pending.label", subtitleKey: "deployments.stats.pending.subtitle", state: "Critical", filter: "Status eq 'PendingEnrollment'" },
	{ key: "expired", labelKey: "deployments.stats.expired.label", subtitleKey: "deployments.stats.expired.subtitle", state: "Error", filter: "Status eq 'EnrollmentExpired'" }
];

interface DeploymentRow {
	Id: string;
	Name: string;
	OwnerOrgId: string;
	Status: string;
}

interface IssueEnrollmentCodeResponse {
	code: string;
	expiresAt: string;
}

// === Add Deployment Dialog ===

interface DeploymentNewState {
	customerOrgId: string;
	customerDisplay: string;
	customerValueState: "None" | "Error";
	name: string;
	nameValueState: "None" | "Error";
	environment: "Production" | "Test" | "Demo" | "Sandbox";
	environmentValueState: "None" | "Error";
	locationId: string;
	locationValueState: "None" | "Error";
	attrTier: string;
	attrEdition: string;
}

const ENVIRONMENTS: readonly DeploymentNewState["environment"][] = ["Production", "Test", "Demo", "Sandbox"];

interface CustomerPickerState {
	searchQuery: string;
	selectedId: string;
	selectedName: string;
	compuTecAllowed: boolean;
}

interface CreateDeploymentRequest {
	ownerOrgId: string;
	locationId: string;
	name: string;
	environmentType: string;
	attributes: Record<string, string> | null;
}

type OwnerType = "Partner" | "CompuTec" | "EndCustomer";

/**
 * Organization types that may own a deployment in the given environment. Mirrors the server rule
 * `deployment.owner_invalid_for_environment` (CreateDeploymentCommand): CompuTec owns only Demo deployments.
 * The picker filter and the picker texts both derive from this one definition.
 */
function ownerTypesFor(environment: DeploymentNewState["environment"]): readonly OwnerType[] {
	return environment === "Demo"
		? ["Partner", "CompuTec", "EndCustomer"]
		: ["Partner", "EndCustomer"];
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * @namespace computec.b2b.portal.controller
 */
export default class Deployments extends ListPageController {
	private codeDialog?: Dialog;
	private reEnrollDialog?: Dialog;
	private reEnrollTargetId?: string;
	private addDeploymentDialog?: Dialog;
	private customerPickerDialog?: Dialog;

	public override onInit(): void {
		const initial: ViewState = {
			statusFilter: "ALL",
			environmentFilter: "ALL",
			searchQuery: "",
			noOwnerOnly: false
		};
		this.getView().setModel(new JSONModel(initial), "view");
		void this.initStatsCards("Deployments", STATS);
		this.getRouter().getRoute("deployments")?.attachPatternMatched(() => { void this.onShow(); });
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const title = bundle.getText("deployments.title") ?? "Deployments";
		this.setPageHeader({ title, subtitle: bundle.getText("deployments.subtitle") ?? "" });
		this.applyFilters();
		void this.refreshStatsCards();
	}

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/searchQuery", raw);
		this.applyFilters();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onEnvironmentSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as EnvKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/environmentFilter", key);
		this.applyFilters();
	}

	public onNoOwnerSelect(event: CheckBox$SelectEvent): void {
		(this.getView().getModel("view") as JSONModel).setProperty("/noOwnerOnly", event.getParameter("selected") === true);
		this.applyFilters();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "standard";
		const preset = VARIANTS[key];
		if (!preset) {
			return;
		}
		const model = this.getView().getModel("view") as JSONModel;
		model.setProperty("/statusFilter", preset.status);
		model.setProperty("/environmentFilter", preset.environment);
		model.setProperty("/searchQuery", preset.search);
		model.setProperty("/noOwnerOnly", false);
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("deploymentTable");
		void this.refreshStatsCards();
	}

	public onOpenDeployment(event: Button$PressEvent): void {
		this.navFromRow(event, "Id", "deploymentDetail");
	}

	public onOpenOwner(event: Button$PressEvent): void {
		this.navToOrganizationFromRow(event, "OwnerOrgId");
	}

	public onOpenPartner(event: Button$PressEvent): void {
		this.navToOrganizationFromRow(event, "PartnerOrgId");
	}

	private navFromRow(event: Button$PressEvent, idProperty: string, route: string): void {
		const id = this.rowId(event, idProperty);
		if (id) {
			this.navTo(route, { id });
		}
	}

	private navToOrganizationFromRow(event: Button$PressEvent, idProperty: string): void {
		const id = this.rowId(event, idProperty);
		if (id) {
			this.navToOrganization(id);
		}
	}

	private rowId(event: Button$PressEvent, idProperty: string): string | undefined {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		return ctx ? ctx.getProperty(idProperty) as string | undefined : undefined;
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		if (state.searchQuery.trim().length > 0) {
			const qLower = escapeOData(state.searchQuery.trim().toLowerCase());
			parts.push(`contains(tolower(Name),'${qLower}')`);
		}
		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}
		if (state.environmentFilter !== "ALL") {
			parts.push(`EnvironmentType eq '${state.environmentFilter}'`);
		}
		if (state.noOwnerOnly) {
			parts.push("OwnerUserId eq null");
		}

		const binding = this.getTableBinding("deploymentTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	// === Create deployment — dialog ===

	public async onAddDeploymentPress(): Promise<void> {
		if (!this.addDeploymentDialog) {
			this.addDeploymentDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AddDeploymentDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addDeploymentDialog);
		}
		this.resetAddDeploymentState();
		this.addDeploymentDialog.open();
	}

	private resetAddDeploymentState(): void {
		// Without orgs.read the owner picker (Organizations feed) is hidden in the dialog: the caller
		// can only own the deployment through their own organization, and the record-scoped CompuTec
		// developer may create Demo deployments only (deployment.owner_invalid_for_environment), so the
		// environment is preset to Demo and locked in the view.
		const ownOrganizationOnly = !this.getOwnerComponent().getPermissions().has("orgs.read");
		const userModel = this.getOwnerComponent().getModel("user") as JSONModel | undefined;
		const ownOrgId = ownOrganizationOnly ? ((userModel?.getProperty("/orgId") as string | undefined) ?? "") : "";
		this.getView().setModel(new JSONModel({
			customerOrgId: ownOrgId,
			customerDisplay: "",
			customerValueState: "None",
			name: "",
			nameValueState: "None",
			environment: ownOrganizationOnly ? "Demo" : "Production",
			environmentValueState: "None",
			locationId: "",
			locationValueState: "None",
			attrTier: "",
			attrEdition: ""
		} satisfies DeploymentNewState), "addDeployment");
		this.getView().setModel(new JSONModel({
			searchQuery: "",
			selectedId: "",
			selectedName: "",
			compuTecAllowed: false
		} satisfies CustomerPickerState), "customerPicker");
	}

	private getAddDeploymentModel(): JSONModel {
		return this.getView().getModel("addDeployment") as JSONModel;
	}

	public onCancelAddDeployment(): void {
		this.addDeploymentDialog?.close();
	}

	/**
	 * Clears the chosen owner when the environment changes: the allowed owner types depend on it
	 * (ownerTypesFor), so the previous choice may no longer be valid - e.g. CompuTec chosen for Demo
	 * is not allowed once the environment is Production.
	 */
	public onEnvironmentChange(): void {
		const model = this.getAddDeploymentModel();
		model.setProperty("/customerOrgId", "");
		model.setProperty("/customerDisplay", "");
		model.setProperty("/customerValueState", "None");
		model.setProperty("/environmentValueState", "None");
	}

	// === Owner picker (allowed owner types: ownerTypesFor) ===

	public async onCustomerValueHelp(): Promise<void> {
		if (!this.customerPickerDialog) {
			this.customerPickerDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.CustomerPickerDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.customerPickerDialog);
		}
		const pickerModel = this.getView().getModel("customerPicker") as JSONModel;
		pickerModel.setProperty("/searchQuery", "");
		pickerModel.setProperty("/selectedId", "");
		pickerModel.setProperty("/selectedName", "");
		const environment = (this.getAddDeploymentModel().getData() as DeploymentNewState).environment;
		pickerModel.setProperty("/compuTecAllowed", ownerTypesFor(environment).includes("CompuTec"));

		this.applyCustomerPickerFilter();
		const table = this.byId("customerPickerTable") as Table | undefined;
		table?.removeSelections(true);

		this.customerPickerDialog.open();
	}

	public onCustomerPickerSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("customerPicker") as JSONModel).setProperty("/searchQuery", raw);
		this.applyCustomerPickerFilter();
	}

	public onCustomerPickerSelectionChange(event: ListBase$SelectionChangeEvent): void {
		const item = event.getParameter("listItem") as ColumnListItem | undefined;
		const ctx = item?.getBindingContext("odata") as Context | null | undefined;
		const pickerModel = this.getView().getModel("customerPicker") as JSONModel;
		if (!ctx) {
			pickerModel.setProperty("/selectedId", "");
			pickerModel.setProperty("/selectedName", "");
			return;
		}
		pickerModel.setProperty("/selectedId", String(ctx.getProperty("Id") ?? ""));
		pickerModel.setProperty("/selectedName", String(ctx.getProperty("Name") ?? ""));
	}

	public onCustomerPickerConfirm(): void {
		const picker = (this.getView().getModel("customerPicker") as JSONModel).getData() as CustomerPickerState;
		if (!UUID_RE.test(picker.selectedId)) {
			return;
		}
		const model = this.getAddDeploymentModel();
		model.setProperty("/customerOrgId", picker.selectedId);
		model.setProperty("/customerDisplay", picker.selectedName);
		model.setProperty("/customerValueState", "None");
		this.customerPickerDialog?.close();
	}

	public onCustomerPickerCancel(): void {
		this.customerPickerDialog?.close();
	}

	private applyCustomerPickerFilter(): void {
		const table = this.byId("customerPickerTable") as Table | undefined;
		const binding = table?.getBinding("items") as ODataListBinding | undefined;
		if (!binding) {
			return;
		}
		const state = (this.getView().getModel("customerPicker") as JSONModel).getData() as CustomerPickerState;
		const env = (this.getAddDeploymentModel().getData() as DeploymentNewState).environment;
		const typeFilter = `(${ownerTypesFor(env).map(type => `Type eq '${type}'`).join(" or ")})`;
		const parts: string[] = [typeFilter];
		if (state.searchQuery.trim().length > 0) {
			const qLower = escapeOData(state.searchQuery.trim().toLowerCase());
			parts.push(`contains(tolower(Name),'${qLower}')`);
		}
		binding.changeParameters({ $filter: parts.join(" and ") });
	}

	// === Submit new deployment ===

	public async onSubmitAddDeployment(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const model = this.getAddDeploymentModel();
		const state = model.getData() as DeploymentNewState;

		const errors: string[] = [];
		if (!UUID_RE.test(state.customerOrgId)) {
			errors.push(bundle.getText("deployments.new.error.customerRequired") ?? "Customer is required");
			model.setProperty("/customerValueState", "Error");
		}
		if (state.name.trim().length === 0) {
			errors.push(bundle.getText("deployments.new.error.nameRequired") ?? "Name is required");
			model.setProperty("/nameValueState", "Error");
		}
		if (!ENVIRONMENTS.includes(state.environment)) {
			errors.push(bundle.getText("deployments.new.error.environmentRequired") ?? "Environment is required");
			model.setProperty("/environmentValueState", "Error");
		}
		if (!UUID_RE.test(state.locationId)) {
			errors.push(bundle.getText("deployments.new.error.locationRequired") ?? "Location is required");
			model.setProperty("/locationValueState", "Error");
		}
		if (errors.length > 0) {
			MessageBox.warning(errors.join("\n"));
			return;
		}

		const attributes = buildDeploymentAttributes(state);
		const payload: CreateDeploymentRequest = {
			ownerOrgId: state.customerOrgId,
			locationId: state.locationId,
			name: state.name.trim(),
			environmentType: state.environment,
			attributes: attributes
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const created = await fetcher.json<{ id: string } | string>("/api/v1/deployments", {
				method: "POST",
				body: JSON.stringify(payload)
			});
			const newId = typeof created === "string" ? created : created.id;
			if (!UUID_RE.test(newId)) {
				throw new Error("Backend did not return new deployment id");
			}
			MessageToast.show(bundle.getText("deployments.add.success") ?? "Deployment created");
			this.getOwnerComponent().getModel("odata")?.refresh();
			this.addDeploymentDialog?.close();
			this.navTo("deploymentDetail", { id: newId });
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.new.failure") ?? "Failed to create deployment"));
		}
	}

	// === Issue enrollment code ===

	public async onIssueCodePress(event: Button$PressEvent): Promise<void> {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		if (!ctx) {
			return;
		}
		const row = ctx.getObject() as DeploymentRow;
		const bundle = await this.getResourceBundle();

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const result = await fetcher.json<IssueEnrollmentCodeResponse>(
				`/api/v1/deployments/${row.Id}/enrollment-code`,
				{ method: "POST" });

			if (!this.codeDialog) {
				this.codeDialog = await Fragment.load({
					id: this.getView().getId(),
					name: "computec.b2b.portal.view.EnrollmentCodeDialog",
					controller: this
				}) as Dialog;
				this.getView().addDependent(this.codeDialog);
			}
			(this.byId("taEnrollmentCode") as TextArea).setValue(result.code);
			(this.byId("txtEnrollmentExpires") as Text).setText(new Date(result.expiresAt).toLocaleString());
			this.codeDialog.open();
			MessageToast.show(bundle.getText("deployments.code.success") ?? "Enrollment code issued");
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.code.failure") ?? "Failed"));
		}
	}

	public onCloseEnrollmentCode(): void {
		this.codeDialog?.close();
	}

	// === Re-enroll ===

	public async onReEnrollPress(event: Button$PressEvent): Promise<void> {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		if (!ctx) {
			return;
		}
		const row = ctx.getObject() as DeploymentRow;
		this.reEnrollTargetId = row.Id;

		if (!this.reEnrollDialog) {
			this.reEnrollDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.ReEnrollDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.reEnrollDialog);
		}
		(this.byId("selReEnrollReason") as Select).setSelectedKey("RoutineRotation");
		this.reEnrollDialog.open();
	}

	public onCancelReEnroll(): void {
		this.reEnrollDialog?.close();
		this.reEnrollTargetId = undefined;
	}

	public async onConfirmReEnroll(): Promise<void> {
		if (!this.reEnrollTargetId) {
			return;
		}
		const reason = (this.byId("selReEnrollReason") as Select).getSelectedKey();
		const bundle = await this.getResourceBundle();
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/deployments/${this.reEnrollTargetId}/re-enroll`, {
				method: "POST",
				body: JSON.stringify({ reason })
			});
			MessageToast.show(bundle.getText("deployments.reenroll.success") ?? "Re-enroll requested");
			this.reEnrollDialog?.close();
			this.reEnrollTargetId = undefined;
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("deployments.reenroll.failure") ?? "Failed"));
		}
	}

}

function buildDeploymentAttributes(state: DeploymentNewState): Record<string, string> | null {
	const result: Record<string, string> = {};
	const tier = state.attrTier.trim();
	const ed = state.attrEdition.trim();
	if (tier) result.Tier = tier;
	if (ed) result.Edition = ed;
	return Object.keys(result).length > 0 ? result : null;
}

function escapeOData(value: string): string {
	return value.replace(/'/g, "''");
}
